import { useEffect, useRef, useState } from 'react';
import '../styles/LandingPage/Phases.css';
import ResearchFoundations from '../PhasesHooks/ResearchFoundations';
import SelectiveAdvisory from '../PhasesHooks/SelectiveAdisory';

const phase1Items = [
  'IFRS 17 & capital framework studies',
  'Solvency & stress testing research',
  'Mortality & morbidity experience analyses',
  'Asset-liability modelling techniques',
  'Climate & parametric insurance frameworks',
  'AI applications in actuarial modelling',
];

const phase2Items = [
  'IFRS 17 technical advisory & peer review',
  'Capital & solvency advisory',
  'Independent model validation',
  'Product & profitability analysis',
  'Regulatory submission support',
];

const statPills = [
  { icon: 'ti-file-certificate', val: 'IFRS 17',    lbl: 'Primary Focus'    },
  { icon: 'ti-chart-line',       val: 'Phase 1',    lbl: 'Currently Active' },
  { icon: 'ti-shield-check',     val: 'Independent', lbl: 'Research Model'  },
];

export default function Phases() {
  const sectionRef = useRef(null);
  const [activeModal, setActiveModal] = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            section.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (e, cardIndex) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    setMousePos({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
    setActiveCard(cardIndex);
  };

  const handleMouseLeave = () => {
    setActiveCard(null);
  };

  const openModal = (modal) => {
    setActiveModal(modal);
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  return (
    <>
      <section className="phases-section" id="about" ref={sectionRef}>

        {/* ── Ambient background layers ── */}
        <div className="phases-ambient">
          <div className="ambient-orb orb-1" />
          <div className="ambient-orb orb-2" />
          <div className="ambient-orb orb-3" />
          <div className="ambient-orb orb-4" />
          <div className="ambient-grid" />
          <div className="ambient-noise" />
          <div className="ambient-mesh">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="mesh" width="40" height="40" patternUnits="userSpaceOnUse">
                  <circle cx="20" cy="20" r="1" fill="rgba(30,207,176,0.08)" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#mesh)" />
            </svg>
          </div>
        </div>

        {/* ── Floating geometric shapes ── */}
        <div className="floating-shapes">
          <div className="float-shape shape-ring" />
          <div className="float-shape shape-diamond" />
          <div className="float-shape shape-circle" />
          <div className="float-shape shape-hex" />
        </div>

        {/* ── Header ── */}
        <div className="phases-header">
          <div className="phases-header-left">
            <div className="s-label">
              <span className="s-label-ping" />
              Our Model
            </div>
            <h2>
              <span className="h2-line">Built on Research.</span>
              <span className="h2-line">Trusted for Advisory.</span>
            </h2>
            <p className="s-intro">
              Actyra follows a deliberate two-phase strategy — establishing intellectual
              authority before engaging commercially. Every engagement is grounded in
              published, defended insight.
            </p>
          </div>

          <div className="phases-stat-row">
            {statPills.map(({ icon, val, lbl }, i) => (
              <div className="stat-pill" key={lbl} style={{ '--si': i }}>
                <div className="stat-pill-glow" />
                <div className="stat-pill-icon-ring">
                  <i className={`ti ${icon}`} aria-hidden="true" />
                </div>
                <div className="stat-pill-text">
                  <span className="stat-pill-val">{val}</span>
                  <span className="stat-pill-lbl">{lbl}</span>
                </div>
                <div className="stat-pill-progress" style={{ '--progress': `${(i + 1) * 33}%` }} />
              </div>
            ))}
          </div>
        </div>

        {/* ── Cards ── */}
        <div className="phases-grid">

          {/* Phase 1 — Teal */}
          <div
            className="phase-card teal"
            style={{ '--ci': 0 }}
            onMouseMove={(e) => handleMouseMove(e, 0)}
            onMouseLeave={handleMouseLeave}
          >
            <div
              className="phase-card-spotlight"
              style={{
                opacity: activeCard === 0 ? 1 : 0,
                background: `radial-gradient(600px circle at ${mousePos.x}% ${mousePos.y}%, rgba(30, 207, 176, 0.1), transparent 40%)`,
              }}
            />
            <div className="phase-card-glow" />
            <div className="phase-card-scan" />
            <div className="phase-card-border-glow" />

            <div className="phase-card-header">
              <div className="phase-icon-wrap teal">
                <i className="ti ti-microscope" aria-hidden="true" />
                <div className="icon-ripple" />
                <div className="icon-ripple icon-ripple-delay" />
              </div>
              <span className="phase-badge badge-teal">
                <span className="badge-dot" />
                Phase 1 · Active
              </span>
            </div>

            <h3>Research-Driven Foundation</h3>
            <p>
              We begin as an independent platform focused on high-impact actuarial
              knowledge. The objective is credibility, not revenue — positioning
              Actyra as a leading knowledge authority.
            </p>

            <div className="phase-divider">
              <div className="divider-shimmer" />
            </div>

            <ul className="phase-list">
              {phase1Items.map((item, i) => (
                <li key={i} style={{ '--li': i }}>
                  <span className="list-check">
                    <i className="ti ti-circle-check" aria-hidden="true" />
                  </span>
                  <span className="list-text">{item}</span>
                  <span className="list-number">{String(i + 1).padStart(2, '0')}</span>
                </li>
              ))}
            </ul>

            <div className="phase-footer">
              <button 
                className="phase-cta" 
                onClick={() => openModal('research')}
                type="button"
              >
                <span className="cta-text">Explore research</span>
                <i className="ti ti-arrow-right" aria-hidden="true" />
              </button>
              <span className="phase-count-badge">
                <span className="count-pulse" />
                6 focus areas
              </span>
            </div>

            <div className="phase-big">01</div>
            <div className="phase-corner tl" />
            <div className="phase-corner tr" />
            <div className="phase-corner bl" />
            <div className="phase-corner br" />
            <div className="phase-card-reflection" />
          </div>

          {/* Phase 2 — Crimson */}
          <div
            className="phase-card crimson"
            style={{ '--ci': 1 }}
            onMouseMove={(e) => handleMouseMove(e, 1)}
            onMouseLeave={handleMouseLeave}
          >
            <div
              className="phase-card-spotlight"
              style={{
                opacity: activeCard === 1 ? 1 : 0,
                background: `radial-gradient(600px circle at ${mousePos.x}% ${mousePos.y}%, rgba(240, 20, 76, 0.08), transparent 40%)`,
              }}
            />
            <div className="phase-card-glow" />
            <div className="phase-card-scan" />
            <div className="phase-card-border-glow" />

            <div className="phase-card-header">
              <div className="phase-icon-wrap crimson">
                <i className="ti ti-briefcase" aria-hidden="true" />
                <div className="icon-ripple" />
                <div className="icon-ripple icon-ripple-delay" />
              </div>
              <span className="phase-badge badge-crimson">
                <span className="badge-dot" />
                Phase 2 · Upcoming
              </span>
            </div>

            <h3>Selective Advisory &amp; Consulting</h3>
            <p>
              Once credibility is established, Actyra transitions into high-value
              consulting — strictly limited to areas where original research has
              already been published and peer-tested.
            </p>

            <div className="phase-divider">
              <div className="divider-shimmer" />
            </div>

            <ul className="phase-list">
              {phase2Items.map((item, i) => (
                <li key={i} style={{ '--li': i }}>
                  <span className="list-chevron">
                    <i className="ti ti-chevron-right" aria-hidden="true" />
                  </span>
                  <span className="list-text">{item}</span>
                  <span className="list-number">{String(i + 1).padStart(2, '0')}</span>
                </li>
              ))}
            </ul>

            <div className="phase-footer">
              <button 
                className="phase-cta" 
                onClick={() => openModal('advisory')}
                type="button"
              >
                <span className="cta-text">Learn more</span>
                <i className="ti ti-arrow-right" aria-hidden="true" />
              </button>
              <span className="phase-count-badge">
                <span className="count-pulse" />
                5 service lines
              </span>
            </div>

            <div className="phase-big">02</div>
            <div className="phase-corner tl" />
            <div className="phase-corner tr" />
            <div className="phase-corner bl" />
            <div className="phase-corner br" />
            <div className="phase-card-reflection" />
          </div>

        </div>
      </section>

      {/* ── Modals ── */}
      {activeModal === 'research' && (
        <ResearchFoundations onClose={closeModal} />
      )}
      {activeModal === 'advisory' && (
        <SelectiveAdvisory onClose={closeModal} />
      )}
    </>
  );
}
